import moment from 'moment';
import { Column, Row, Text } from '~/commonComponents';
import {
  AuthorizationStatus,
  CredentialV2TypeEnum,
} from '~/domain/Credentials/types/Credentials';
import type {
  IAuthorization,
  IAuthorizationCredential,
  ICredentialNpiData,
  ICredentialNumberData,
} from '~/domain/Credentials/types/Credentials';
import type { ProfessionTypeEnum } from '~/models';
import { Colors } from '~/themes/colors';

interface IVerifiedCredentialsProps {
  authorizations: IAuthorization[];
  jobProfession?: ProfessionTypeEnum;
}

const getCredentialDetails = (credential: IAuthorizationCredential) => {
  if (
    credential.type === CredentialV2TypeEnum.Number &&
    credential.data &&
    'number' in credential.data
  ) {
    const numberData = credential?.data as ICredentialNumberData;

    return {
      licenseNumber: numberData?.number?.credentialNumber,
      issuedYear: credential?.issuedAt ? moment(credential.issuedAt).format('YYYY') : null,
    };
  }

  if (
    credential.type === CredentialV2TypeEnum.Npi &&
    credential.data &&
    'npi' in credential.data
  ) {
    const numberData = credential?.data as ICredentialNpiData;

    return {
      licenseNumber: numberData?.npi?.npiNumber,
    };
  }
  return null;
};

const CredentialItem = ({ credential }: { credential: IAuthorizationCredential }) => {
  const details = getCredentialDetails(credential);

  return (
    <Row alignItems="center" gap={8}>
      <Text fontSize={16} color={Colors.neutral_600}>
        {'\u2022'}
      </Text>
      <Row alignItems="center" gap={8} flexWrap="wrap">
        <Text fontSize={16} color={Colors.neutral_600}>
          {credential.credentialName.replaceAll('NPI Number', 'NPI License').replaceAll(' Number', '')}
        </Text>
        {details?.licenseNumber ? (
          <Text fontSize={22} color={Colors.neutral_200}>
            |
          </Text>
            ) : null}
        {details?.licenseNumber ? (
          <Row alignItems="center" gap={4}>
            <Text bold fontSize={16} color={Colors.neutral_600}>
              License #:
            </Text>
            <Text fontSize={16} color={Colors.neutral_600}>
              {details?.licenseNumber}
            </Text>
          </Row>
            ) : null}
        {details?.licenseNumber && details.issuedYear ? (
          <Text fontSize={22} color={Colors.neutral_200}>
            |
          </Text>
            ) : null}
        {details?.issuedYear ? (
          <Row alignItems="center" gap={4}>
            <Text bold fontSize={16} color={Colors.neutral_600}>
              Issued:
            </Text>
            <Text fontSize={16} color={Colors.neutral_600}>
              {details.issuedYear}
            </Text>
          </Row>
            ) : null}
      </Row>
    </Row>
  );
};

export const VerifiedCredentials = ({
  authorizations,
  jobProfession,
}: IVerifiedCredentialsProps) => {
  const verifiedCredentials = authorizations
    .flatMap((auth) =>
      auth.credentials.filter(
        (c) =>
          (!jobProfession || jobProfession === auth.profession) &&
          c.status === AuthorizationStatus.Verified &&
          !c.isDraft,
      ),
    )
    .filter(
      (c, index, self) =>
        self.findIndex((other) => other.credentialName === c.credentialName) === index,
    );

  if (verifiedCredentials.length === 0) {
    return null;
  }

  return (
    <Column gap={4}>
      <Text bold fontSize={20} color={Colors.neutral_600}>
        Verified Credentials:
      </Text>
      <Column gap={8} paddingLeft={4}>
        {verifiedCredentials.map((credential) => (
          <CredentialItem key={credential.id ?? credential.credential} credential={credential} />
        ))}
      </Column>
    </Column>
  );
};
